// Lightweight in-memory TTL cache + async memoizer.
// Used to cache rarely-changing reference data (taxonomy) for fast repeat loads.
const store = new Map();

export function cacheGet(key) {
  const entry = store.get(key);
  if (!entry) return undefined;
  if (entry.expires && entry.expires < Date.now()) {
    store.delete(key);
    return undefined;
  }
  return entry.value;
}

export function cacheSet(key, value, ttlMs = 5 * 60 * 1000) {
  store.set(key, { value, expires: ttlMs ? Date.now() + ttlMs : 0 });
  return value;
}

export function cacheClear(key) {
  if (key) store.delete(key);
  else store.clear();
}

// Wrap an async function so repeated calls within the TTL reuse the result.
// De-dupes concurrent in-flight requests for the same key.
const inflight = new Map();
export async function cached(key, fn, ttlMs = 5 * 60 * 1000) {
  const hit = cacheGet(key);
  if (hit !== undefined) return hit;
  if (inflight.has(key)) return inflight.get(key);
  const promise = (async () => {
    try {
      const value = await fn();
      cacheSet(key, value, ttlMs);
      return value;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, promise);
  return promise;
}
